import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, Car, FileText, DollarSign, CalendarIcon, Plus, Edit, Trash2, MapPin, Phone, Mail, CreditCard, Eye, X } from 'lucide-react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import api from '../../utils/api';
import { FaFileInvoice } from 'react-icons/fa';
import ReactApexChart from 'react-apexcharts';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

interface Client {
  id: number;
  email: string;
  raison_social: string | null;
  first_name: string;
  last_name: string;
  phone: string | null;
  address: string | null;
  ice: string | null;
  rib: string | null;
  latitude: number | null;
  longitude: number | null;
}

interface Vehicle {
  id: number;
  brand_id: number;
  plate_number: string;
  model: string;
  fuel_type: 'gasoline' | 'diesel' | 'electric' | 'hybrid';
  year: number | null;
  kilometers: number | null;
  vin_number: string | null;
  conductor_password: string | null;
  last_service_date: string | null;
  next_service_date: string | null;
}

interface ServiceOrder {
  id: number;
  vehicle_id: number;
  service_id: number;
  technician_id: number;
  status: string;
  description: string;
  final_price: number;
  created_at: string;
  completed_at: string | null;
  vehicle: { plate_number: string };
  service: { name: string };
  technician: { first_name: string; last_name: string };
}

interface Brand {
  id: number;
  name: string;
}

export default function ClientDetails() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);

  const [client, setClient] = useState<Client | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [serviceOrders, setServiceOrders] = useState<ServiceOrder[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [revenueData, setRevenueData] = useState<any>(null);
  const [vehicleDialog, setVehicleDialog] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Partial<Vehicle> | null>(null);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('vehicles');
  const [toast, setToast] = useState({ visible: false, severity: '', message: '' });

  useEffect(() => {
    fetchClientData();
    fetchBrands();
  }, [id]);

  useEffect(() => {
    if (client && client.latitude && client.longitude && mapContainer.current) {
      initializeMap();
    }
  }, [client]);

  const initializeMap = () => {
    if (map.current || !client || !client.latitude || !client.longitude) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [client.longitude, client.latitude],
      zoom: 12
    });

    new mapboxgl.Marker()
      .setLngLat([client.longitude, client.latitude])
      .addTo(map.current);
  };

  const fetchClientData = async () => {
    try {
      setLoading(true);
      const [clientRes, vehiclesRes, serviceOrdersRes, summaryRes, revenueRes] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get(`/clients/${id}/vehicles`),
        api.get(`/clients/${id}/service-orders`),
        api.get(`/clients/${id}/summary`),
        api.get(`/clients/${id}/revenue`)
      ]);

      setClient(clientRes.data);
      setVehicles(vehiclesRes.data);
      setServiceOrders(serviceOrdersRes.data);
      setSummary(summaryRes.data);
      setRevenueData(revenueRes.data);
    } catch (error) {
      console.error('Error fetching client data:', error);
      showToast('error', t('error_fetching_client_data'));
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await api.get('/brands');
      setBrands(response.data.data);
    } catch (error) {
      console.error('Error fetching brands:', error);
      showToast('error', t('error_fetching_brands'));
    }
  };

  const openNewVehicle = () => {
    setEditingVehicle({});
    setVehicleDialog(true);
  };

  const hideVehicleDialog = () => {
    setVehicleDialog(false);
    setEditingVehicle(null);
  };

  const saveVehicle = async () => {
    if (!editingVehicle) return;

    try {
      if (editingVehicle.id) {
        await api.put(`/vehicles/${editingVehicle.id}`, editingVehicle);
        showToast('success', t('vehicle_updated'));
      } else {
        await api.post(`/clients/${id}/vehicles`, { ...editingVehicle, client_id: id });
        showToast('success', t('vehicle_created'));
      }
      hideVehicleDialog();
      fetchClientData();
    } catch (error) {
      console.error('Error saving vehicle:', error);
      showToast('error', t('error_saving_vehicle'));
    }
  };

  const editVehicle = (vehicle: Vehicle) => {
    setEditingVehicle({ ...vehicle });
    setVehicleDialog(true);
  };

  const confirmDeleteVehicle = (vehicle: Vehicle) => {
    if (window.confirm(t('delete_vehicle_confirmation', { plate: vehicle.plate_number }))) {
      deleteVehicle(vehicle.id);
    }
  };

  const deleteVehicle = async (vehicleId: number) => {
    try {
      await api.delete(`/vehicles/${vehicleId}`);
      showToast('success', t('vehicle_deleted'));
      fetchClientData();
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      showToast('error', t('error_deleting_vehicle'));
    }
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('fr-FR', { style: 'currency', currency: 'MAD' });
  };

  const chartOptions = {
    chart: {
      type: 'bar' as const,
      height: 350
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
      },
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent']
    },
    xaxis: {
      categories: revenueData ? revenueData.labels : [],
    },
    yaxis: {
      title: {
        text: t('revenue') + ' (MAD)'
      }
    },
    fill: {
      opacity: 1
    },
    tooltip: {
      y: {
        formatter: function (val: number) {
          return formatCurrency(val);
        }
      }
    }
  };

  const chartSeries = [
    {
      name: t('revenue'),
      data: revenueData ? revenueData.data : []
    }
  ];

  const showToast = (severity: string, message: string) => {
    setToast({ visible: true, severity, message });
    setTimeout(() => setToast({ ...toast, visible: false }), 3000);
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse bg-gray-200 h-40 mb-4 rounded-lg"></div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="animate-pulse bg-gray-200 h-24 rounded-lg"></div>
          ))}
        </div>
        <div className="animate-pulse bg-gray-200 h-96 rounded-lg"></div>
      </div>
    );
  }

  if (!client) {
    return <div className="text-center text-2xl font-bold mt-8">{t('client_not_found')}</div>;
  }

  return (
    <div className="p-4">
      {toast.visible && (
        <div className={`fixed top-4 right-4 p-4 rounded-md shadow-md ${
          toast.severity === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white`}>
          {toast.message}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <h1 className='text-2xl font-bold text-black'>{client.raison_social}</h1>
            <div className="flex items-center">
              <User className="mr-2" size={18} />
              <span>{`${client.first_name} ${client.last_name}`}</span>
            </div>
            <div className="flex items-center">
              <Phone className="mr-2" size={18} />
              <span>{client.phone || t('not_provided')}</span>
            </div>
            <div className="flex items-center">
              <Mail className="mr-2" size={18} />
              <span>{client.email}</span>
            </div>
            <div className="flex items-center">
              <MapPin className="mr-2" size={18} />
              <span>{client.address || t('not_provided')}</span>
            </div>
            <div className="flex items-center">
              <FaFileInvoice className="mr-2" size={18} />
              <span>{client.ice || t('not_provided')}</span>
            </div>
            <div className="flex items-center">
              <CreditCard className="mr-2" size={18} />
              <span>{client.rib || t('not_provided')}</span>
            </div>
          </div>
          <div ref={mapContainer} className="h-64 md:h-auto"></div>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-lg font-semibold">{summary.total_vehicles}</div>
                <div className="text-sm text-gray-500">{t('total_vehicles')}</div>
              </div>
              <Car className="text-blue-500" size={24} />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-lg font-semibold">{summary.total_service_orders}</div>
                <div className="text-sm text-gray-500">{t('total_service_orders')}</div>
              </div>
              <FileText className="text-green-500" size={24} />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-lg font-semibold">{formatCurrency(summary.total_spent)}</div>
                <div className="text-sm text-gray-500">{t('total_spent')}</div>
              </div>
              <DollarSign className="text-yellow-500" size={24} />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-lg font-semibold">
                  {summary.last_service_date ? new Date(summary.last_service_date).toLocaleDateString() : t('no_services')}
                </div>
                <div className="text-sm text-gray-500">{t('last_service_date')}</div>
              </div>
              <CalendarIcon className="text-purple-500" size={24} />
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md">
        <div className="flex border-b">
          <button
            className={`px-4 py-2 font-medium ${activeTab === 'vehicles' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('vehicles')}
          >
            {t('vehicles')}
          </button>
          <button
            className={`px-4 py-2 font-medium ${activeTab === 'service_orders' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('service_orders')}
          >
            {t('service_orders')}
          </button>
          <button
            className={`px-4 py-2 font-medium ${activeTab === 'revenue' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('revenue')}
          >
            {t('revenue')}
          </button>
        </div>

        <div className="p-4">
          {activeTab === 'vehicles' && (
            <>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold">{t('vehicles')}</h3>
                <button
                  onClick={openNewVehicle}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition duration-200"
                >
                  <Plus className="inline-block mr-2" size={18} />
                  {t('add_vehicle')}
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('plate_number')}</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('brand')}</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('model')}</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('fuel_type')}</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('year')}</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('kilometers')}</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('last_service')}</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('next_service')}</th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">{t('actions')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {vehicles.map((vehicle) => (
                      <tr key={vehicle.id}>
                        <td className="px-6 py-4 whitespace-nowrap">{vehicle.plate_number}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{brands.find(b => b.id === vehicle.brand_id)?.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{vehicle.model}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{t(vehicle.fuel_type)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{vehicle.year}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{vehicle.kilometers}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{vehicle.last_service_date ? new Date(vehicle.last_service_date).toLocaleDateString() : '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{vehicle.next_service_date ? new Date(vehicle.next_service_date).toLocaleDateString() : '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => editVehicle(vehicle)}
                            className="text-indigo-600 hover:text-indigo-900 mr-2"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => confirmDeleteVehicle(vehicle)}
                            className="text-red-600 hover:text-red-900 mr-2"
                          >
                            <Trash2 size={18} />
                          </button>
                          <button
                            onClick={() => navigate(`/vehicles/${vehicle.id}`)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Eye size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {activeTab === 'service_orders' && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('order_id')}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('vehicle')}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('service')}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('technician')}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('status')}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('price')}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('created_at')}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('completed_at')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {serviceOrders.map((order) => (
                    <tr key={order.id}>
                      <td className="px-6 py-4 whitespace-nowrap">{order.id}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{order.vehicle.plate_number}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{order.service.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{`${order.technician.first_name} ${order.technician.last_name}`}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          order.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {t(`order_status_${order.status}`)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{formatCurrency(order.final_price)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{new Date(order.created_at).toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{order.completed_at ? new Date(order.completed_at).toLocaleString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'revenue' && revenueData && (
            <div className="h-96">
              <ReactApexChart 
                options={chartOptions} 
                series={chartSeries} 
                type="bar" 
                height="100%" 
              />
            </div>
          )}
        </div>
      </div>

      {vehicleDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold">{editingVehicle && editingVehicle.id ? t('edit_vehicle') : t('new_vehicle')}</h2>
              <button onClick={hideVehicleDialog} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); saveVehicle(); }} className="p-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="brand_id" className="block text-sm font-medium text-gray-700">{t('brand')}</label>
                  <select
                    id="brand_id"
                    value={editingVehicle?.brand_id || ''}
                    onChange={(e) => setEditingVehicle({ ...editingVehicle, brand_id: parseInt(e.target.value) })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  >
                    <option value="">{t('select_brand')}</option>
                    {brands.map((brand) => (
                      <option key={brand.id} value={brand.id}>{brand.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="plate_number" className="block text-sm font-medium text-gray-700">{t('plate_number')}</label>
                  <input
                    type="text"
                    id="plate_number"
                    value={editingVehicle?.plate_number || ''}
                    onChange={(e) => setEditingVehicle({ ...editingVehicle, plate_number: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="model" className="block text-sm font-medium text-gray-700">{t('model')}</label>
                  <input
                    type="text"
                    id="model"
                    value={editingVehicle?.model || ''}
                    onChange={(e) => setEditingVehicle({ ...editingVehicle, model: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="fuel_type" className="block text-sm font-medium text-gray-700">{t('fuel_type')}</label>
                  <select
                    id="fuel_type"
                    value={editingVehicle?.fuel_type || ''}
                    onChange={(e) => setEditingVehicle({ ...editingVehicle, fuel_type: e.target.value as Vehicle['fuel_type'] })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    required
                  >
                    <option value="">{t('select_fuel_type')}</option>
                    <option value="gasoline">{t('gasoline')}</option>
                    <option value="diesel">{t('diesel')}</option>
                    <option value="electric">{t('electric')}</option>
                    <option value="hybrid">{t('hybrid')}</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="year" className="block text-sm font-medium text-gray-700">{t('year')}</label>
                  <input
                    type="number"
                    id="year"
                    value={editingVehicle?.year || ''}
                    onChange={(e) => setEditingVehicle({ ...editingVehicle, year: parseInt(e.target.value) || null })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                </div>
                <div>
                  <label htmlFor="kilometers" className="block text-sm font-medium text-gray-700">{t('kilometers')}</label>
                  <input
                    type="number"
                    id="kilometers"
                    value={editingVehicle?.kilometers || ''}
                    onChange={(e) => setEditingVehicle({ ...editingVehicle, kilometers: parseInt(e.target.value) || null })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                </div>
                <div>
                  <label htmlFor="vin_number" className="block text-sm font-medium text-gray-700">{t('vin_number')}</label>
                  <input
                    type="text"
                    id="vin_number"
                    value={editingVehicle?.vin_number || ''}
                    onChange={(e) => setEditingVehicle({ ...editingVehicle, vin_number: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                </div>
                <div>
                  <label htmlFor="conductor_password" className="block text-sm font-medium text-gray-700">{t('conductor_password')}</label>
                  <input
                    type="text"
                    id="conductor_password"
                    value={editingVehicle?.conductor_password || ''}
                    onChange={(e) => setEditingVehicle({ ...editingVehicle, conductor_password: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={hideVehicleDialog}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t('save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

